import React, { useState } from 'react';
import { 
  BarChart3, Layers, Search, 
  ChevronDown, ChevronRight, Download,
  ArrowLeft, Filter, Plus
} from 'lucide-react';
import { 
  billOfMaterials
} from '../../data/mrpData';
import { products } from '../../data/inventoryData';

const MRPBomView = ({ setActiveView }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Get product details
  const getProductDetails = (productCode) => {
    return products.find(p => p.sku === productCode) || { name: '未知產品', sku: productCode };
  };
  
  // Get all BOM categories
  const categories = ['all', ...new Set(billOfMaterials.map(bom => bom.category || '未分類'))];
  
  // Filter BOMs based on search and category
  const filteredBoms = billOfMaterials.filter(bom => {
    const product = getProductDetails(bom.product_code);
    const matchesSearch = searchTerm === '' || 
      bom.product_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || bom.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  // Toggle expanded state for a BOM item
  const toggleExpand = (bomId) => {
    setExpandedItems(prev => ({
      ...prev,
      [bomId]: !prev[bomId]
    }));
  };
  
  // Calculate BOM statistics
  const bomStats = {
    totalBoms: billOfMaterials.length,
    totalComponents: billOfMaterials.reduce((sum, bom) => sum + (bom.components ? bom.components.length : 0), 0),
    avgComponentsPerBom: Math.round(billOfMaterials.reduce((sum, bom) => sum + (bom.components ? bom.components.length : 0), 0) / billOfMaterials.length)
  };
  
  // Render BOM tree item recursively
  const renderBomItem = (item, level = 0, parentPath = '') => {
    const product = getProductDetails(item.product_code || item.component_code);
    const hasChildren = item.components && item.components.length > 0;
    const itemId = item.bom_id || `${parentPath}-${item.component_code}`;
    const isExpanded = expandedItems[itemId];
    
    return (
      <div key={itemId} className="border-b border-gray-200 last:border-b-0">
        <div 
          className={`flex items-center py-3 px-4 hover:bg-gray-50 ${level > 0 ? 'pl-' + (level * 8 + 4) + 'px' : ''}`}
          style={{ paddingLeft: level > 0 ? `${level * 24 + 16}px` : '16px' }}
        >
          {hasChildren ? (
            <button 
              onClick={() => toggleExpand(itemId)}
              className="p-1 rounded-full hover:bg-gray-200 mr-2"
            >
              {isExpanded ? 
                <ChevronDown className="w-4 h-4 text-gray-600" /> : 
                <ChevronRight className="w-4 h-4 text-gray-600" />
              }
            </button>
          ) : (
            <div className="w-6 mr-2"></div>
          )}
          
          <div className="flex-1">
            <div className="flex items-center">
              <span className="font-medium text-gray-900">{product.name}</span>
              <span className="ml-2 text-sm text-gray-500">{item.product_code || item.component_code}</span>
              {item.category && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                  {item.category}
                </span>
              )}
            </div>
            {item.description && (
              <p className="text-sm text-gray-500 mt-0.5">{item.description}</p>
            )}
          </div>
          
          {item.quantity && (
            <div className="text-right">
              <div className="text-sm font-medium">{item.quantity} {item.unit || '個'}</div>
              {item.cost && (
                <div className="text-xs text-gray-500">單價: ${item.cost.toFixed(2)}</div>
              )}
            </div>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="border-t border-gray-100">
            {item.components.map(component => renderBomItem(component, level + 1, itemId))}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => setActiveView('dashboard')} 
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-2xl font-bold text-gray-800">物料清單 (BOM)</h2>
        </div>
        <div className="flex space-x-2">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center">
            <Plus className="w-4 h-4 mr-2" />
            新增BOM
          </button>
          <button className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center text-sm">
            <Download className="w-4 h-4 mr-2" />
            匯出
          </button>
        </div>
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">BOM總數</p>
              <p className="text-2xl font-bold text-gray-800">{bomStats.totalBoms}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Layers className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">組件總數</p>
              <p className="text-2xl font-bold text-gray-800">{bomStats.totalComponents}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <BarChart3 className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">平均組件數</p>
              <p className="text-2xl font-bold text-gray-800">{bomStats.avgComponentsPerBom}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Filter className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="搜尋產品代碼或名稱..."
              />
            </div>
          </div>
          
          <div className="w-64">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              {categories.map((category, index) => (
                <option key={index} value={category}>
                  {category === 'all' ? '所有類別' : category}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* BOM List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">物料清單</h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {filteredBoms.length > 0 ? (
            filteredBoms.map(bom => renderBomItem(bom))
          ) : (
            <div className="p-6 text-center text-gray-500">
              未找到符合條件的物料清單
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MRPBomView;
